import React, { useState, useRef, useEffect } from "react";
import { FormFeedback, Input, FormGroup } from "reactstrap";

const SearchableDropdown = ({
  options = [],
  placeholder = "Search...",
  value,
  onChange,
  onSearch,
  onLoadMore,
  hasMore = true,
  debounceMs = 700,
  required,
  name,
  searchKey,
  disabled = false,
  isSearchable = true,
  error,
  isClear = false,
}) => {
  const wrapperRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);
  const [filteredOptions, setFilteredOptions] = useState(options);

  const [dropdown, setDropdown] = useState({
    query: "",
    open: false,
  });

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  /* ------------------------------
     Sync selected value → label
     (ONLY when user selects)
  ------------------------------- */
  useEffect(() => {
    if (!dropdown.open && value && options.length) {
      const selected = options.find(
        (opt) => String(opt.value) === String(value),
      );

      setDropdown((prev) => ({
        ...prev,
        query: selected?.label || "",
      }));
    }

    if (!value && !dropdown.open) {
      setDropdown((prev) => ({ ...prev, query: "" }));
    }
  }, [value, options]);

  /* ------------------------------
     Debounced search
  ------------------------------- */
  useEffect(() => {
    if (!isSearchable || !onSearch) return;

    clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      onSearch({
        key: searchKey || name,
        query: dropdown.query,
      });
    }, debounceMs);

    return () => clearTimeout(debounceRef.current);
  }, [dropdown.query, isSearchable, onSearch, searchKey, name, debounceMs]);

  useEffect(() => {
    if (!isSearchable || !dropdown.open) return;

    // clearTimeout(debounceRef.current);

    // debounceRef.current = setTimeout(() => {
    const query = dropdown.query.trim().toLowerCase();

    // if search box is empty → show all options
    if (!query) {
      setFilteredOptions(options);
      return;
    }

    const key = searchKey || "label";

    const result = options.filter((item) =>
      String(item[key] ?? "")
        .toLowerCase()
        .includes(query),
    );

    setFilteredOptions(result);
    // }, debounceMs);

    // return () => clearTimeout(debounceRef.current);
  }, [dropdown.query, dropdown.open, options]);

  /* ------------------------------
     Infinite scroll
  ------------------------------- */
  const handleScroll = () => {
    if (!listRef.current || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      onLoadMore?.(name);
    }
  };

  /* ------------------------------
     Outside click close
  ------------------------------- */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setDropdown((prev) => ({ ...prev, open: false }));
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <FormGroup style={{ marginBottom: 0 }}>
      <div
        ref={wrapperRef}
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Input
          type="text"
          name={name}
          placeholder={placeholder}
          value={dropdown.query}
          readOnly={!isSearchable}
          disabled={disabled}
          invalid={!!error}
          required={required}
          autoComplete="off"
          style={{
            backgroundColor: disabled ? "#e9ecef" : "#fff",
            paddingRight: "30px", // 🔥 space for icon
          }}
          onFocus= {isClear ? () => setDropdown((prev) => ({ ...prev, open: true })) : () => setDropdown({ query: "", open: true })}
          // onFocus=
          onChange={(e) => {
            if (!isSearchable) return;

            const newValue = e.target.value;

            setDropdown({
              query: newValue,
              open: true,
            });

            onChange?.({
              target: {
                name,
                value: "",
              },
            });
          }}
        />

        {/* Dropdown icon */}
        <span
          style={{
            position: "absolute",
            right: "10px",
            pointerEvents: "auto",
            cursor: "pointer",
          }}
          onClick={() =>
            // !disabled && setDropdown((prev) => ({ ...prev, open: !prev.open }))
            !disabled && setDropdown({ query: "", open: true })
          }
        >
          <i className="fa fa-angle-down" />
        </span>
        {/* Dropdown list */}
        {dropdown.open && (
          <div
            ref={listRef}
            onScroll={handleScroll}
            className="dropdown-menu show w-100 mt-1"
            style={{ maxHeight: "220px", overflowY: "auto" }}
          >
            {filteredOptions.length ? (
              filteredOptions.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={`dropdown-item ${item.disabled ? "disabled" : ""}`}
                  disabled={item.disabled}
                  style={{
                    cursor: item.disabled ? "not-allowed" : "pointer",
                    opacity: item.disabled ? 0.6 : 1,
                  }}
                  onClick={() => {
                    if (item.disabled) return;

                    setDropdown({
                      query: item.label,
                      open: false,
                    });

                    onChange?.({
                      target: {
                        name,
                        value: item.value,
                      },
                    });
                  }}
                >
                  {item.label}
                </button>
              ))
            ) : (
              <span className="dropdown-item text-muted">No results found</span>
            )}
          </div>
        )}
      </div>

      {/* ✅ Error text OUTSIDE the positioned wrapper */}
      {error && <FormFeedback className="d-block">{error}</FormFeedback>}
    </FormGroup>
  );
};

export default SearchableDropdown;
